/**
 * Office profile page - dynamic candidates list
 *
 * Embeds a candidate browse/search control filtered to candidates
 * for a specific office. Reads office data from data attributes on
 * the #office-candidates container element.
 */

import { EntityIndex } from './base';
import { canonicalPath } from '@electinfo/common';
import type { EntityConfig, EntitySearchResult } from './types';

class OfficeProfileCandidates extends EntityIndex {
  private officeFilter = '';

  protected setup(): void {
    // Read office data from container before base setup
    const container = document.getElementById('office-candidates');
    if (!container) return;

    const officeType = container.dataset.officeType || '';
    const state = container.dataset.officeState || '';
    const district = container.dataset.officeDistrict || '';

    const parts: string[] = [];
    if (officeType) parts.push(`office:${officeType}`);
    if (state) parts.push(`office_state:${state}`);
    if (district) parts.push(`office_district:${district}`);
    this.officeFilter = parts.join(',');

    // Build context-aware placeholder text
    if (district && state) {
      this.searchPlaceholder = `Search candidates for this district...`;
    } else if (state && !officeType) {
      this.searchPlaceholder = `Search candidates for this state's offices...`;
    } else {
      this.searchPlaceholder = `Search candidates for this office...`;
    }

    super.setup();
  }

  private searchPlaceholder = 'Search candidates for this office...';

  getConfig(): EntityConfig {
    return {
      entityType: 'Politician',
      placeholder: this.searchPlaceholder,
      browseTitle: 'Candidates',
      sortField: 'total_contributions',
      sortOrder: 'DESC',
      fixedFilter: this.officeFilter,
      formatResult: (result: EntitySearchResult) => {
        const party = result.party || 'Unknown Party';

        let contributions = '';
        if (result.metadata) {
          try {
            const meta = JSON.parse(result.metadata);
            if (meta.total_contributions > 0) {
              contributions = ` | $${Math.round(meta.total_contributions).toLocaleString()}`;
            }
          } catch { /* ignore parse errors */ }
        }

        return {
          url: `/${canonicalPath('Politician', result.id)}/`,
          name: result.name || '',
          meta: `${party}${contributions}`
        };
      }
    };
  }

  /**
   * Insert the search UI into the #office-candidates container
   * instead of the default location (after header).
   */
  protected insertSearchUI(): void {
    const config = this.getConfig();
    const targetContainer = document.getElementById('office-candidates');
    if (!targetContainer) return;

    const container = document.createElement('div');
    container.className = 'entity-search-container';
    container.innerHTML = `
      <form class="entity-search-form" id="entity-search-form">
        <input type="text" id="entity-search-input" placeholder="${config.placeholder}" autocomplete="off">
        <button type="submit">Search</button>
      </form>
      <div class="entity-search-results" id="entity-search-results">
        <div class="entity-search-results-header">
          <h3 id="entity-results-title">${config.browseTitle}</h3>
          <button type="button" class="entity-search-clear" id="entity-search-clear" style="display: none;">Show All</button>
        </div>
        <div id="entity-search-results-list"></div>
        <div id="entity-pagination"></div>
      </div>
    `;

    targetContainer.appendChild(container);
  }
}

new OfficeProfileCandidates();
